// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable/Clickable";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { mergeWithCss } from "@west/XDL/Utilities";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks, shallowEqual } from "react-redux";

interface OwnProps {
    category?: Category;
    showImages?: boolean;
    extendedStyles?: CategoryBoxViewStyles;
}

type Props = OwnProps;

export interface CategoryBoxViewStyles {
    innerContainer?: InjectableCss;
    subCategoryImageItem?: InjectableCss;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    innerInformationContainer?: InjectableCss;
    subCategoryNameLinksListItem?: GridItemProps;
    subCategoryViewAllItem?: GridItemProps;
    subCategoryNameLinkInner?: LinkPresentationProps;
    viewAllLink?: LinkPresentationProps;
}

export const categoryBoxViewStyles: CategoryBoxViewStyles = {};

const CategoryBoxView: FC<Props> = ({ category, showImages, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(categoryBoxViewStyles, extendedStyles));
    const { xGoogleAnalytics } = useAppSelector(state => {
        return {
            xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        };
    }, shallowEqual);

    if (!category) {
        return null;
    }
    // XNG-Changes
    const categoryLinkClickHandler = (url: string, categoryLinkLabel?: string) => {
        if (xGoogleAnalytics.productCategoryClickEvent && url) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.productCategoryClickEventName,
                click_text: categoryLinkLabel,
                destination: url,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };
    return (
        <StyledWrapper {...styles.innerContainer}>
            <StyledWrapper {...styles.innerInformationContainer}>
                <StyledWrapper {...styles.subCategoryImageItem}>
                    {showImages && (
                        <Clickable
                            href={category.path}
                            onClick={() => categoryLinkClickHandler(category.path, category.shortDescription)}
                            {...styles.subCategoryImageClickable}
                            aria-label={category.shortDescription}
                        >
                            <LazyImage
                                src={category.smallImagePath}
                                altText={category.imageAltText}
                                {...styles.subCategoryImage}
                            />
                        </Clickable>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.subCategoryNameLinkItem}>
                    <Link
                        href={category.path}
                        onClick={() => categoryLinkClickHandler(category.path, category.shortDescription)}
                        {...styles.subCategoryNameLink}
                        aria-label={category.shortDescription}
                        data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                    >
                        {category.shortDescription}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.subCategoryNameLinksListItem}>
                {category.subCategoryIds &&
                    category.subCategoryIds.slice(0, 5).map(subCategoryId => (
                        <SubCategoryLinkInner
                            categoryId={subCategoryId}
                            key={subCategoryId}
                            subCategoryNameLinkInnerStyles={styles.subCategoryNameLinkInner}
                            // XNG-Changes:
                            onClick={categoryLinkClickHandler}
                        />
                    ))}
            </StyledWrapper>
            <StyledWrapper {...styles.subCategoryViewAllItem}>
                <Link
                    href={category.path}
                    {...styles.viewAllLink}
                    data-test-selector={`categoryDetailsSubCategoriesViewAll_${category.id}`}
                >
                    {translate("View All")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const SubCategoryLinkInnerView = ({
    category,
    subCategoryNameLinkInnerStyles,
    onClick,
}: ReturnType<typeof linkInnerMapStateToProps> & {
    subCategoryNameLinkInnerStyles?: CategoryBoxViewStyles["subCategoryNameLinkInner"];
    onClick: (url: string, categoryLabel?: string) => void;
}) => {
    if (!category) {
        return null;
    }

    return (
        <Link
            href={category.path}
            onClick={() => onClick(category.path, category.shortDescription)}
            {...subCategoryNameLinkInnerStyles}
        >
            {category.shortDescription}
        </Link>
    );
};

const linkInnerMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLinkInner = connect(linkInnerMapStateToProps)(SubCategoryLinkInnerView);

export default CategoryBoxView;
